import { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Search, Package, PackageCheck, Truck, XCircle, MessageCircle } from "lucide-react";
import PageShell from "@/components/PageShell";
import { inr } from "@/shop/products";
import { API } from "@/shop/api";

const PAYMENT_STYLE = {
  paid: "border-[#7ED321]/40 bg-[#7ED321]/10 text-[#7ED321]",
  cod_pending: "border-yellow-500/40 bg-yellow-500/10 text-yellow-300",
  failed: "border-red-500/40 bg-red-500/10 text-red-300",
  created: "border-white/20 bg-white/5 text-white/60",
};

const PAYMENT_LABEL = {
  paid: "Paid",
  cod_pending: "Cash on Delivery",
  failed: "Payment failed",
  created: "Payment pending",
};

const STEPS = [
  { key: "confirmed", label: "Confirmed", icon: Package },
  { key: "dispatched", label: "Dispatched", icon: Truck },
  { key: "delivered", label: "Delivered", icon: PackageCheck },
];

const Stepper = ({ status }) => {
  const activeIdx = Math.max(0, STEPS.findIndex((s) => s.key === status));
  return (
    <div className="mt-6 flex items-start" data-testid="track-stepper">
      {STEPS.map((s, i) => {
        const reached = i <= activeIdx;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex flex-1 items-start last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                  reached ? "border-[#7ED321] bg-[#7ED321]/15 text-[#7ED321]" : "border-white/15 text-white/30"
                }`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wide ${reached ? "text-[#7ED321]" : "text-white/30"}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`mx-2 mt-5 h-px flex-1 ${i < activeIdx ? "bg-[#7ED321]" : "bg-white/10"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const OrderCard = ({ o }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid={`track-result-${o.order_number}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <span className="font-display text-2xl text-white">{o.order_number}</span>
        <p className="mt-1 text-xs text-white/40">
          {new Date(o.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          {o.first_name && ` · ${o.first_name}`}
          {o.pincode && ` · ${o.pincode}`}
        </p>
      </div>
      <span
        className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${PAYMENT_STYLE[o.payment_status] || PAYMENT_STYLE.created}`}
        data-testid={`track-payment-${o.order_number}`}
      >
        {PAYMENT_LABEL[o.payment_status] || o.payment_status}
      </span>
    </div>

    <ul className="mt-4 space-y-1.5 text-sm">
      {o.items.map((i, idx) => (
        <li key={idx} className="flex justify-between gap-4 text-white/70">
          <span>{i.title} × {i.qty}</span>
          <span className="shrink-0">{inr(i.price * i.qty)}</span>
        </li>
      ))}
    </ul>
    <div className="mt-3 flex justify-between border-t border-white/10 pt-3 text-sm">
      <span className="text-xs uppercase tracking-[0.15em] text-white/40">Total</span>
      <span className="font-semibold text-white">{inr(o.total)}</span>
    </div>

    {o.fulfilment_status === "cancelled" ? (
      <div className="mt-6 flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4" data-testid={`track-cancelled-${o.order_number}`}>
        <XCircle className="h-5 w-5 shrink-0 text-red-300" />
        <p className="text-sm text-red-200">
          This order was cancelled. If you think this is a mistake, message us on WhatsApp with your order number.
        </p>
      </div>
    ) : (
      <Stepper status={o.fulfilment_status} />
    )}
  </div>
);

const TrackOrder = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    setOrders(null);
    try {
      const { data } = await axios.post(`${API}/orders/track`, { query: query.trim() });
      setOrders(data.orders);
    } catch (err) {
      setError(err.response?.data?.detail || "No order found. Check the details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <Helmet>
        <title>Track Your Order | Zentra</title>
        <meta name="description" content="Check the live status of your Zentra protein bar order with your order number or phone number." />
      </Helmet>
      <section className="px-5 py-16 lg:px-12 lg:py-24" data-testid="track-page">
        <div className="mx-auto max-w-xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#7ED321]">Order Tracking</p>
          <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] text-white sm:text-5xl" data-testid="track-heading">
            Where's my order?
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-[#A6A66A]">
            Enter your order number (ZNT-…), or the phone number you ordered with, to see the live status.
          </p>

          <form onSubmit={submit} className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="track-form">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ZNT-123456 or 10-digit mobile number"
              data-testid="track-input"
              className="w-full flex-1 rounded-full border border-white/15 bg-[#0A0A0A] px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#7ED321] focus:ring-1 focus:ring-[#7ED321]"
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              data-testid="track-submit-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#7ED321] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331] disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              {loading ? "Checking…" : "Track"}
            </button>
          </form>

          {error && (
            <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="track-error">
              {error}
            </div>
          )}
        </div>

        {orders && (
          <div className="mx-auto mt-10 max-w-2xl space-y-6" data-testid="track-results">
            {orders.map((o) => (
              <OrderCard key={o.order_number} o={o} />
            ))}
            <p className="text-center text-xs text-white/40">
              Questions about your order?{" "}
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "919003140803"}?text=${encodeURIComponent(`Hi Zentra! Checking on my order ${orders[0].order_number}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="track-whatsapp-link"
                className="inline-flex items-center gap-1 font-medium text-[#25D366] hover:underline"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Message us on WhatsApp
              </a>
            </p>
          </div>
        )}
      </section>
    </PageShell>
  );
};

export default TrackOrder;
